import React, { useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { MelaButton, applyMelaThemeBranding } from "@/ui/mela";
import {
  isChunkLoadError,
  isChunkReloadPending,
  reloadAfterChunkError,
} from "@/utils/chunk-reload";

const ChunkErrorFallback = () => {
  // Not suspending: this may render before the translations have loaded, or
  // because loading them failed.
  const { t } = useTranslation(undefined, { useSuspense: false });

  // Rendered above MelaProvider when the app shell itself failed to load, so the
  // Mela tokens may not be on the document yet.
  useLayoutEffect(() => {
    applyMelaThemeBranding();
  }, []);

  return (
    <div
      role="alert"
      className="flex h-screen w-full flex-col items-center justify-center gap-4 p-4 text-center"
    >
      <p>
        {t("error.genericError.translation", {
          defaultValue: "Oops... Something went wrong. Please try again.",
        })}
      </p>
      <MelaButton
        variant="solid"
        tone="primary"
        dataTestId="chunk-error-reload-button"
        onClick={() => window.location.reload()}
      >
        {t("update.prompt.dialog.confirm.translation", {
          defaultValue: "Reload Now",
        })}
      </MelaButton>
    </div>
  );
};

interface ChunkErrorBoundaryProps {
  children: React.ReactNode;
  /** Clears a shown error when it changes, e.g. the pathname after navigating away. */
  resetKey?: unknown;
}

interface ChunkErrorBoundaryState {
  error: unknown;
}

/**
 * Catches lazily loaded code that failed to load. A missing chunk triggers one
 * guarded reload (see `reloadAfterChunkError`); any other error, or a chunk
 * that is still missing after that reload, shows a reload prompt instead of a
 * blank page. Reporting stays with the root `onCaughtError` handler.
 */
export class ChunkErrorBoundary extends React.Component<
  ChunkErrorBoundaryProps,
  ChunkErrorBoundaryState
> {
  state: ChunkErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): ChunkErrorBoundaryState {
    return { error };
  }

  componentDidUpdate(prevProps: ChunkErrorBoundaryProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: unknown) {
    if (isChunkLoadError(error) && reloadAfterChunkError()) {
      this.forceUpdate();
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (isChunkLoadError(error) && isChunkReloadPending()) return null;
    return <ChunkErrorFallback />;
  }
}
